//import { TanStackDevtools } from '@tanstack/react-devtools'
import { createRootRoute, HeadContent, Scripts } from '@tanstack/react-router'
//import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'

import Footer from '../components/Footer'
import Header from '../components/Header'

import appCss from '../styles.css?inline'

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8'
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1'
      },
      {
        title: 'Online HTML Editor: Free Online WYSIWYG HTML Editor'
      },
      {
        name: 'description',
        content:
          'Free online HTML Editor with an intuitive WYSIWYG Editor that outputs clean W3C compliant HTML code that you can copy-paste into any project.'
      },
      { property: 'og:type', content: 'website' },
      {
        property: 'og:title',
        content: 'Online HTML Editor: Free Online WYSIWYG HTML Editor'
      },
      {
        property: 'og:description',
        content:
          'Free online HTML Editor with an intuitive WYSIWYG Editor that outputs clean W3C compliant HTML code that you can copy-paste into any project.'
      },
      { property: 'og:url', content: 'https://htmleditor.io/' },
      { property: 'og:locale', content: 'en_US' },
      {
        property: 'og:site_name',
        content: 'HTML Editor | WYSIWYG HTML Edit Online'
      },
      {
        property: 'og:image',
        content: 'https://htmleditor.io/images/htmleditor.png'
      },
      { property: 'og:image:width', content: '3066' },
      { property: 'og:image:height', content: '1620' },
      {
        property: 'og:image:alt',
        content: 'HTML Editor — free online WYSIWYG HTML editor'
      },
      { name: 'twitter:card', content: 'summary_large_image' },
      {
        name: 'twitter:description',
        content:
          'Free online HTML Editor with an intuitive WYSIWYG Editor that outputs clean W3C compliant HTML code that you can copy-paste into any project.'
      },
      {
        name: 'twitter:title',
        content: 'Online HTML Editor: Free Online WYSIWYG HTML Editor'
      },
      {
        name: 'twitter:image',
        content: 'https://htmleditor.io/images/htmleditor.png'
      },
      { name: 'msapplication-TileColor', content: '#2b5797' },
      { name: 'theme-color', content: '#ffffff' }
    ],
    links: [
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png'
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png'
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png'
      },
      { rel: 'manifest', href: '/site.webmanifest' },
      { rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: '#5bbad5' }
    ],
    scripts: [
      // Google Analytics
      {
        src: 'https://www.googletagmanager.com/gtag/js?id=G-F4NWPF1257',
        async: true
      },
      {
        children: `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-F4NWPF1257');
        `
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': ['WebApplication', 'SoftwareApplication'],
          name: 'HTML Editor',
          url: 'https://htmleditor.io/',
          description:
            'Free online HTML Editor with an intuitive WYSIWYG Editor that outputs clean W3C compliant HTML code that you can copy-paste into any project.',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'All',
          browserRequirements:
            'Requires a modern web browser with JavaScript enabled.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          }
        })
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is an HTML editor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'An HTML editor is a tool for creating and editing HTML markup. A WYSIWYG (What You See Is What You Get) HTML editor lets you compose content visually while it generates clean HTML in real time, so you can copy the output into any web project without writing tags by hand.'
              }
            },
            {
              '@type': 'Question',
              name: 'How does a WYSIWYG HTML editor work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'You type and format content in a visual editing surface, and the editor renders it the way a browser would. As you make changes, the editor produces the equivalent HTML markup in a paired source view that you can copy or refine directly.'
              }
            },
            {
              '@type': 'Question',
              name: 'What should I look for in an online HTML editor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Clean copy-paste behavior, text styling and color controls, table and list support, image and link insertion, and HTML output that conforms to W3C standards. A browser-based editor that requires no install or sign-up is ideal for quick edits.'
              }
            },
            {
              '@type': 'Question',
              name: 'How do I use an online HTML editor?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Open the editor in your browser, type or paste your content, and apply headings, formatting, links, images, and tables from the toolbar. The editor generates HTML in the source pane in real time — copy it into your website, email, or blog.'
              }
            },
            {
              '@type': 'Question',
              name: 'Is the HTML Editor free?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. HTML Editor is completely free, runs entirely in your browser, and requires no registration or download.'
              }
            }
          ]
        })
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'HTML Editor',
          legalName: 'Digital Rocket Factory Oy',
          url: 'https://htmleditor.io/',
          logo: 'https://htmleditor.io/images/htmleditor.png'
        })
      }
    ]
  }),

  shellComponent: RootDocument
})

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
      </head>
      <body className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
        {/*<TanStackDevtools
          config={{
            position: 'bottom-right'
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />
            }
          ]}
        />*/}
        <Scripts />
      </body>
    </html>
  )
}
